/**
 * SEO head and structured data for the Terms of Service (`/terms`).
 *
 * Constants only, like every other `src/lib/*-seo.ts`: the route tree imports
 * each route file eagerly for its `head()`, so whatever this module imports
 * lands in the entry chunk of every page. The Markdown source and its renderer
 * live in `terms-of-service.ts`, imported from the route's code-split
 * component alone.
 */

import { isProduction } from "@/config/config";
import { PUBLISHER_NODE, SITE_URL, WEBSITE_NODE } from "@/lib/marketing-jsonld";

export const TERMS_URL = `${SITE_URL}terms`;

/** Two to four words, like the privacy policy's document title. */
export const TERMS_DOCUMENT_TITLE = "GoodSender Terms of Service";

export const TERMS_DESCRIPTION =
  "The terms for using GoodSender: accounts, consent-based sending, your blog content, AI assistants and the MCP connector, fees and liability.";

const TERMS_OG_IMAGE = "https://goodsender.com/og-image.png";

/**
 * The terms' "Last updated" day, YYYY-MM-DD. A literal, not a parse of the
 * Markdown: this module is on the eager route-tree path, so it must not import
 * the terms text or a parser. `terms-of-service-page.test.ts` asserts it
 * equals `termsLastUpdated(terms)`, so a revision that forgets this line fails
 * CI instead of shipping a stale sitemap lastmod.
 */
export const TERMS_LAST_UPDATED = "2026-11-02";

export const termsJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${TERMS_URL}#terms`,
  url: TERMS_URL,
  name: TERMS_DOCUMENT_TITLE,
  description: TERMS_DESCRIPTION,
  inLanguage: "en",
  dateModified: TERMS_LAST_UPDATED,
  isPartOf: WEBSITE_NODE,
  publisher: PUBLISHER_NODE,
};

export const termsBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Terms", item: TERMS_URL },
  ],
};

type HeadElement = { type: "meta" | "link"; props: Record<string, string> };

const TERMS_SEO_HEAD_ELEMENTS: HeadElement[] = [
  { type: "meta", props: { name: "description", content: TERMS_DESCRIPTION } },
  {
    type: "meta",
    props: {
      name: "robots",
      content: isProduction
        ? "index, follow, max-image-preview:large"
        : "noindex, nofollow",
    },
  },
  { type: "meta", props: { name: "author", content: "Joy Labs Ventures" } },
  { type: "link", props: { rel: "canonical", href: TERMS_URL } },
  // Open Graph
  { type: "meta", props: { property: "og:type", content: "website" } },
  { type: "meta", props: { property: "og:url", content: TERMS_URL } },
  { type: "meta", props: { property: "og:title", content: TERMS_DOCUMENT_TITLE } },
  { type: "meta", props: { property: "og:description", content: TERMS_DESCRIPTION } },
  { type: "meta", props: { property: "og:image", content: TERMS_OG_IMAGE } },
  { type: "meta", props: { property: "og:image:width", content: "1200" } },
  { type: "meta", props: { property: "og:image:height", content: "630" } },
  { type: "meta", props: { property: "og:site_name", content: "GoodSender" } },
  { type: "meta", props: { property: "og:locale", content: "en_US" } },
  // Twitter Card
  { type: "meta", props: { name: "twitter:card", content: "summary_large_image" } },
  { type: "meta", props: { name: "twitter:title", content: TERMS_DOCUMENT_TITLE } },
  { type: "meta", props: { name: "twitter:description", content: TERMS_DESCRIPTION } },
  { type: "meta", props: { name: "twitter:image", content: TERMS_OG_IMAGE } },
];

/** Route-level `head()` for /terms (same contract as the other marketing heads). */
export function termsRouteHead() {
  return {
    meta: [
      { title: TERMS_DOCUMENT_TITLE },
      ...TERMS_SEO_HEAD_ELEMENTS.filter((e) => e.type === "meta").map((e) => e.props),
    ],
    links: TERMS_SEO_HEAD_ELEMENTS.filter((e) => e.type === "link").map((e) => e.props),
  };
}
